import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { Storage, ref, uploadBytesResumable } from '@angular/fire/storage';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  IPatient,
  IReferral,
  IReferralClinicalNote,
  IReferralClinicalNotesChanged,
  IReferralMedia,
  ReferralActor,
  isStafferActor,
} from '@principle-theorem/principle-core/interfaces';
import { buildUploadFilePath } from '@principle-theorem/ng-shared';
import { asyncForEach, WithRef } from '@principle-theorem/shared';
import { ReferralMediaService } from '../../services/referral-media.service';
import { ReferralAddClinicalNotesComponent } from '../referral-add-clinical-notes/referral-add-clinical-notes.component';

@Component({
  selector: 'pr-referral-attachment-menu',
  imports: [
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    ReferralAddClinicalNotesComponent,
  ],
  templateUrl: './referral-attachment-menu.component.html',
  styleUrl: './referral-attachment-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralAttachmentMenuComponent {
  private _storage = inject(Storage);
  referralMedia = inject(ReferralMediaService);

  referral = input.required<WithRef<IReferral>>();
  patient = input.required<WithRef<IPatient>>();
  currentUser = input<ReferralActor>();
  media = input<WithRef<IReferralMedia>[]>([]);
  clinicalNotes = input<WithRef<IReferralClinicalNote>[]>([]);
  uploadFileOnly = input(false);
  hideClinicalNotes = input(false);
  layout = input<'menu' | 'expanded'>('menu');
  title = input('Add Files');

  clinicalNotesChanged = output<IReferralClinicalNotesChanged>();

  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;

  openFileDialog(): void {
    this.fileInput?.nativeElement?.click();
  }

  async startUpload(event: Event): Promise<void> {
    const element = event.target as HTMLInputElement;
    if (!element.files || !element.files.length) {
      return;
    }

    await this._uploadFiles(Array.from(element.files));

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  async attachFromPatientFiles(): Promise<void> {
    const actor = this.currentUser();

    if (!actor || !isStafferActor(actor)) {
      return;
    }

    await this.referralMedia.selectPatientFiles(
      this.patient(),
      this.referral(),
      actor,
      this.media()
    );
  }

  private async _uploadFiles(files: File[]): Promise<void> {
    const referral = this.referral();
    const actor = this.currentUser();

    if (!referral?.ref?.path || !actor) {
      return;
    }

    await asyncForEach(files, async (file) => {
      const storagePath = buildUploadFilePath(
        referral.ref.path,
        file.name,
        true
      );

      const uploadTask = uploadBytesResumable(
        ref(this._storage, storagePath),
        file
      );

      await this.referralMedia.add(
        {
          task: uploadTask,
          file,
          storagePath,
        },
        referral,
        actor
      );
    });
  }
}
